import React from "react";
import styles from "./Packages.module.css";
import { DateResolution, PackageId, formatPrice, priceFor } from "./pricing";
import {
  blueHeron,
  peacock,
  silkieChicken,
  whiteFlowers,
  whiteHummingbird,
  whiteOsprey,
  quail,
  swan,
  finch,
  OspreyOptions,
  Lyrebird,
  Nightingale,
  Speaker,
  Parrot,
  BartenderPackages,
  Toucan,
  Flamingo,
} from "../../Assets";

// ── Constants ─────────────────────────────────────────────────────────────

const SUMMIT_MAILTO =
  "mailto:summitsoundsystem@gmail.com?subject=Pretty Bird Weddings Event Inquiry&body=Name: %0D%0AContact Email: %0D%0APhone number: %0D%0ADates desired: %0D%0APackage: %0D%0ANumber of Guests: %0D%0AAny Additional Items or Questions: ";

// ── Sub-components ────────────────────────────────────────────────────────

// Price for the visitor's chosen date. The sticky bar above carries the date,
// season and day-type context, so each package needs only its own number.
const PackagePrice = ({
  pkg,
  resolution,
}: {
  pkg: PackageId;
  resolution: DateResolution;
}) => {
  if (resolution.status !== "ok") return null;
  const { year, tier, dayType } = resolution.context;
  return (
    <div className={styles.priceBlock}>
      <span className={styles.priceAmount}>
        {formatPrice(priceFor(pkg, year, tier, dayType))}
      </span>
      <span className={styles.priceNote}>
        event + overnight stay · plus tax
      </span>
    </div>
  );
};

// For the five main venue packages (Hummingbird–Peacock):
// title lives outside the content box as a heading; divider is inside the bullet container.
const VenuePackageSection = ({
  image,
  title,
  pkg,
  resolution,
  onGetQuote,
  bullets,
}: {
  image: string;
  title: string;
  pkg: PackageId;
  resolution: DateResolution;
  onGetQuote: (pkg: PackageId, title: string) => void;
  bullets: React.ReactNode[];
}) => (
  <>
    <div className={styles.packageImageContainer}>
      <img
        src={image}
        style={{ width: "300px", mixBlendMode: "screen" }}
        alt=""
      />
    </div>
    <p className={styles.packageTitle}>{title}</p>
    <PackagePrice pkg={pkg} resolution={resolution} />
    {resolution.status === "ok" && (
      <div className={styles.quoteButtonRow}>
        <button
          type="button"
          className={styles.quoteButton}
          onClick={() => onGetQuote(pkg, title.replace(/ Package$/, ""))}
        >
          Get a quote
        </button>
      </div>
    )}
    <div className={styles.contentBox}>
      {bullets.map((bullet, i) => (
        <li key={i} data-list-text="➢">
          <p
            className={styles.venueBullet}
            style={{ paddingTop: i === 0 ? "11pt" : "1pt" }}
          >
            {bullet}
          </p>
        </li>
      ))}
      <hr className={styles.venueDivider} />
    </div>
  </>
);

// For add-on service sections (Quail, Floral, DJ, Bartending, etc.):
// title and bullets live inside the content box; divider is outside.
const ServiceSection = ({
  image,
  imageWidth = "300px",
  title,
  bullets,
}: {
  image?: string;
  imageWidth?: string;
  title?: string;
  bullets: React.ReactNode[];
}) => (
  <>
    {image && (
      <div className={styles.packageImageContainer}>
        <img
          src={image}
          style={{ width: imageWidth, mixBlendMode: "screen" }}
          alt=""
        />
      </div>
    )}
    <div className={styles.contentBox}>
      {title && <p className={styles.packageTitle}>{title}</p>}
      {bullets.map((bullet, i) => (
        <li key={i} data-list-text="➢">
          <p
            className={styles.serviceBullet}
            style={{
              marginTop: i === 0 ? "-10px" : undefined,
              paddingTop: i > 0 ? "1pt" : undefined,
            }}
          >
            {bullet}
          </p>
        </li>
      ))}
      <br />
    </div>
    <hr className={styles.serviceDivider} />
  </>
);

// ── Main component ────────────────────────────────────────────────────────

const ConvertedPackages = ({
  resolution,
  onGetQuote,
}: {
  resolution: DateResolution;
  onGetQuote: (pkg: PackageId, title: string) => void;
}) => (
  <div className={styles.wrapper}>
    <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
    <p style={{ textIndent: "0pt", textAlign: "left" }}>
      <br />
    </p>
    <h1 className={styles.pageTitle}>
      Pretty Bird Weddings and Events Packages
    </h1>
    <ul id="l1">
      {/* ── Venue Packages ─────────────────────────────────────────────── */}

      <VenuePackageSection
        image={whiteHummingbird}
        title="Hummingbird Package"
        pkg="hummingbird"
        resolution={resolution}
        onGetQuote={onGetQuote}
        bullets={[
          "Pricing includes tables and chairs for up to 130 guests, use of the lodge house (sleeps 16), barn, amphitheater, and the grounds. Horses are available for visiting and photography but not for riding",
          "Basic lighting and decor in the barn to create a warm and inviting ambiance",
          "Use of the bridal suite and groom's room for getting ready",
          "Full kitchen allowing for meal preparation and catering in lodge house",
          "Ample parking for guests",
          "Packages for catering, equipment rental, photography, setup and cleanup are booked separately. Tables and chairs will be available in the barn in a standard arrangement",
          "Clients are permitted to bring in their own food, bar service, and photographers if desired",
          "We also require event insurance for all events. Insurance is typically $100 - $400 depending on event details",
        ]}
      />
      <p />

      <VenuePackageSection
        image={whiteOsprey}
        title="Osprey Package"
        pkg="osprey"
        resolution={resolution}
        onGetQuote={onGetQuote}
        bullets={[
          "Includes everything in the Hummingbird Package",
          "Tablecloths, plates, silverware, and place settings for up to 130 guests",
          "Glass vase centerpieces for tables (flowers not included)",
          <>
            Click{" "}
            <a
              href={OspreyOptions}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8cbcff" }}
            >
              here
            </a>{" "}
            to see images of available decor items.
          </>,
        ]}
      />
      <p />

      <VenuePackageSection
        image={blueHeron}
        title="Blue Heron Package"
        pkg="blueHeron"
        resolution={resolution}
        onGetQuote={onGetQuote}
        bullets={[
          "Includes everything in the Hummingbird Package",
          "Additional tables and chairs, setup and takedown included for up to 220 total guest capacity. Note: 220 fits very tightly while seated at tables in the barn. Additional tables may be added outdoors, but tents are not provided, and parking can be tight with larger groups",
        ]}
      />
      <p style={{ textIndent: "0pt", textAlign: "left" }}>
        <br />
      </p>

      <VenuePackageSection
        image={silkieChicken}
        title="Silkie Chicken Package"
        pkg="silkieChicken"
        resolution={resolution}
        onGetQuote={onGetQuote}
        bullets={[
          "Includes everything in the Blue Heron Package",
          "Tablecloths, plates, silverware, and place settings for up to 220 guests",
          "Glass vase centerpieces for tables (flowers not included)",
        ]}
      />
      <p style={{ textIndent: "0pt", textAlign: "left" }}>
        <br />
      </p>

      <VenuePackageSection
        image={peacock}
        title="Peacock Package"
        pkg="peacock"
        resolution={resolution}
        onGetQuote={onGetQuote}
        bullets={[
          "Includes everything in the Silkie Chicken Package",
          "Venue coordinator for setup and takedown/cleanup",
          <>
            $600 credit towards your choice of the following:
            <ul>
              <li style={{ marginTop: "10px" }}>
                Any of our{" "}
                <a href="/rentals" target="_blank" rel="noreferrer">
                  decor rental equipment
                </a>
              </li>
              <li style={{ marginTop: "10px" }}>
                Additional hours of wedding coordination services through{" "}
                <a
                  href="mailto:betsy-robertson@outlook.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  Betsy Robertson
                </a>
              </li>
              <li style={{ marginTop: "10px" }}>
                Lyrebird/Nightingale sound system packages through{" "}
                <a href={SUMMIT_MAILTO} target="_blank" rel="noreferrer">
                  summitsoundsystem@gmail.com
                </a>
              </li>
              <li style={{ marginTop: "10px" }}>
                Floral arrangements with{" "}
                <a
                  href="https://www.lulusartistryflorist.company/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Lulu's Artistry Florist Company
                </a>
              </li>
            </ul>
          </>,
        ]}
      />

      <br />
      <br />
      <br />

      {/* ── Catering ───────────────────────────────────────────────────── */}

      <ServiceSection
        image={quail}
        title="Quail Catering Package"
        bullets={[
          "Price: $500 deposit (fully refundable upon event cancellation)",
          <>
            Exquisite cuisine provided through{" "}
            <a
              style={{ color: "#57d2ff" }}
              href="https://pacificnorthwestcatering.com"
              target="_blank"
            >
              Pacific Northwest Catering
            </a>
          </>,
          <>
            Receive 5% off on the food prices listed in the menus by filling out
            the form at{" "}
            <a
              style={{ color: "#57d2ff" }}
              href="https://www.pacificnorthwestcatering.com/request-form"
              target="_blank"
            >
              https://www.pacificnorthwestcatering.com/request-form
            </a>{" "}
            and booking with Pretty Bird. Tablecloths and place settings will be
            provided when purchased in conjunction with an Osprey, Silkie
            Chicken, or Peacock package. For parties booking a Hummingbird or
            Blue Heron package, tablecloths are available at a rate of $15 per
            tablecloth.
          </>,
          "Pricing to be adjusted based on menu items selected and number of guests. Your discount is secured by booking a package through Pretty Bird",
          "Menu selection and final head count are due one month prior to your event, and final price will be adjusted at that time",
        ]}
      />

      <br />
      <br />
      <br />

      {/* ── Floral Packages ────────────────────────────────────────────── */}

      <ServiceSection
        image={whiteFlowers}
        title="Floral Packages"
        bullets={[
          <>
            All floral packages are provided by{" "}
            <a
              href="https://www.lulusartistryflorist.company"
              target="_blank"
              style={{ color: "#57d2ff" }}
            >
              Lulu's Artistry Florist Company
            </a>
          </>,
          "All packages include Roses, Hydrangeas, premium greenery, and seasonal flowers and have customizable color schemes upon request",
          <>
            Disclaimer: Our packages are uniquely designed and only customizable
            in terms of color palette. For smaller floral requests, our à la
            carte services are available. To order à la carte services, please
            access the form on our website at{" "}
            <a
              href="https://www.lulusartistryflorist.company/contact-us"
              target="_blank"
              style={{ color: "#57d2ff" }}
            >
              https://www.lulusartistryflorist.company/contact-us
            </a>
          </>,
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={finch}
        title="Finch Package"
        bullets={[
          "Price: $2,700 plus tax",
          "Bud vases for up to 22 tables",
          "One specially crafted bridal bouquet",
          "Up to 10 boutonnières",
          "Up to 7 bridesmaids bouquets",
          "Wedding arbor arrangement for center of arbor",
          "Small Sweetheart table arrangement",
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={swan}
        title="Swan Package"
        bullets={[
          "Price: $4600 plus tax",
          "Includes everything in the finch package",
          "Bud Vases for up to 15 additional tables",
          "Two arrangements for front of wedding aisle",
          "Fresh rose petals for flower girls",
          "Two floral corsages",
          "Guestbook Centerpiece",
          "Bar and beverage arrangement",
        ]}
      />

      <br />
      <br />
      <br />

      {/* ── DJ & Sound System Packages ─────────────────────────────────── */}

      <ServiceSection
        image={Speaker}
        title="DJ and Sound System Packages"
        bullets={[
          "Customized packages are available to fit any desired vision and budget. Mix and match items from either package!",
          "Equipment rentals are available for two sound systems for $1,000, or you can add DJ services with one of the below packages.",
          <>
            Please email Summit Sound System for arrangements at{" "}
            <a href={SUMMIT_MAILTO} target="_blank">
              summitsoundsystem@gmail.com
            </a>
            .
          </>,
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={Lyrebird}
        imageWidth="420px"
        title="Lyrebird Sound System Package"
        bullets={[
          "Price: $2,000 for weekdays and $2,500 for weekends",
          "Two sound systems to cover ceremony and reception areas",
          "Wireless microphones, mixers, and speakers using the latest and greatest technology",
          "Everything needed to amplify speeches, make announcements, and play music",
          "DJ services including mixing microphones and queuing your choice of music",
          <>
            Booking inquiries:{" "}
            <a href={SUMMIT_MAILTO} target="_blank">
              summitsoundsystem@gmail.com
            </a>
          </>,
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={Nightingale}
        title="Nightingale DJ and Entertainment package"
        bullets={[
          "Price: $3,000 for weekdays and $3,500 for weekends",
          "Includes everything in the Lyrebird package",
          "Photo booth that can send images/videos to your phone",
          "Configurable LED lighting that can sync to music",
          "Two professional DJs to spin music, take requests, assist with the photo booth and lighting, and help with any technical adjustments as needed to make your celebration flawless and perfect!",
          <>
            Booking inquiries:{" "}
            <a href={SUMMIT_MAILTO} target="_blank">
              summitsoundsystem@gmail.com
            </a>
          </>,
        ]}
      />

      <br />
      <br />
      <br />
      <br />
      <br />
      <br />

      {/* ── Bartending Packages ────────────────────────────────────────── */}

      <ServiceSection
        image={BartenderPackages}
        title="Bartending Packages"
        bullets={[
          <>
            Licensed & insured bartending provided through{" "}
            <a
              href="https://pugetsoundpours.com"
              target="_blank"
              rel="noreferrer"
            >
              Puget Sound Pours
            </a>
            .
          </>,
          "Client supplies the alcohol; a detailed shopping list will be provided.",
          "Pricing to be adjusted based on number of guests and services requested.",
          <>
            Receive 5% off by inquiring via our{" "}
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLScfPwOl5dWxXBUM15pXLngLZ7IQoaH2KSRbpdB3cwhaMJa2aQ/viewform"
              target="_blank"
              rel="noreferrer"
            >
              form
            </a>
            .
          </>,
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        title="All Packages Include"
        bullets={[
          "Licensed Professional Bartender(s)",
          "General Liability & Liquor Liability Insurance",
          "Initial Consultation & Menu Planning",
          "90 min set-up & 60 minute cleanup",
          "4 Hours of Service",
          "Ice & Coolers",
          "Standard Bar Tools & Custom Menu Sign",
          "Self-Serve Water Station",
          "Self Serve Non-Alcoholic Station with Two Options",
          "Check-In 30 days & 7 days before your event",
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={Parrot}
        imageWidth="320px"
        title="Parrot Package"
        bullets={["Beer & Wine", "Starting at $7 per person"]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={Toucan}
        imageWidth="320px"
        title="Toucan Package"
        bullets={[
          "Beer, Wine, and Basic Mixers and Garnishes for Open Bar",
          "Starting at $9 per person",
        ]}
      />

      <br />
      <br />
      <br />

      <ServiceSection
        image={Flamingo}
        imageWidth="320px"
        title="Flamingo Package"
        bullets={[
          "Beer, Wine, and Two Signature Cocktails",
          "Starting at $11 per person",
        ]}
      />

      <br />
      <br />
      <br />

    </ul>
  </div>
);

export default ConvertedPackages;
